import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Pencil, Trash2, Upload } from 'lucide-react';
import { desktopApi } from '../../lib/desktopApi';
import { searchStudents } from '../../lib/consignesSearch.mjs';
import WarningBadge from '../WarningBadge';

const EMPTY = { last_name: '', first_name: '', room: '', bed: '', departure: '', class_name: '', floor: '' };
const FIELDS = [
  ['room', 'Chambre', 'Ex. 115'],
  ['bed', 'Lit', 'A ou B'],
  ['last_name', 'Nom', ''],
  ['first_name', 'Prénom', ''],
  ['departure', 'Départ', 'JJ.MM.AAAA'],
  ['class_name', 'Classe', 'Facultatif'],
  ['floor', 'Étage', 'Facultatif'],
];
const inputClass = 'h-11 w-full rounded-md border border-input bg-background px-3 text-base min-[1025px]:text-sm';

function errorText(error, fallback) {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const StudentForm = ({ initial, onSubmit, onCancel, busy, label }) => {
  const [values, setValues] = useState({ ...EMPTY, ...(initial || {}) });
  return (
    <form
      onSubmit={(event) => { event.preventDefault(); onSubmit(values); }}
      className="grid grid-cols-2 gap-3 rounded-lg border border-primary/40 p-3 sm:grid-cols-4"
      data-testid="student-form"
    >
      {FIELDS.map(([field, title, placeholder]) => (
        <label key={field} className={field === 'last_name' || field === 'first_name' ? 'col-span-2 sm:col-span-1' : ''}>
          <span className="mb-1 block text-xs font-semibold text-muted-foreground">{title}</span>
          <input
            value={values[field] || ''}
            onChange={(event) => setValues((previous) => ({ ...previous, [field]: event.target.value }))}
            placeholder={placeholder}
            data-testid={`student-${field}`}
            className={inputClass}
          />
        </label>
      ))}
      <div className="col-span-2 flex flex-col gap-2 sm:col-span-4 sm:flex-row">
        <button type="submit" disabled={busy} className="h-11 rounded-md bg-primary px-5 font-semibold text-primary-foreground disabled:opacity-60" data-testid="student-save">
          {label}
        </button>
        <button type="button" onClick={onCancel} className="h-11 rounded-md border border-border px-5">Annuler</button>
      </div>
    </form>
  );
};

/** Administration de la liste des étudiants : ajout, correction, retrait, import CSV/XLSX avec relecture. */
const StudentsAdmin = ({ adminToken }) => {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [confirmId, setConfirmId] = useState('');
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sourceLabel, setSourceLabel] = useState('');
  const [confirmReplace, setConfirmReplace] = useState(false);

  const load = useCallback(async () => {
    if (!adminToken) return;
    try {
      setData(await desktopApi.getStudentsAsAdmin(adminToken));
    } catch (error) {
      toast.error('Liste des étudiants indisponible');
    }
  }, [adminToken]);

  useEffect(() => { load(); }, [load]);

  const students = useMemo(() => data?.students || [], [data]);
  const filtered = useMemo(() => searchStudents(students, query), [students, query]);

  const run = async (action, success) => {
    setBusy(true);
    try {
      await action();
      if (success) toast.success(success);
      await load();
      return true;
    } catch (error) {
      toast.error(errorText(error, 'Action impossible'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const pickFile = async (event) => {
    const chosen = event.target.files?.[0];
    event.target.value = '';
    if (!chosen) return;
    if (chosen.size > 1.5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (maximum 1,5 Mo)');
      return;
    }
    setBusy(true);
    setConfirmReplace(false);
    try {
      const content = await readAsBase64(chosen);
      const result = await desktopApi.importStudents({ filename: chosen.name, content_base64: content, mode: 'preview' }, adminToken);
      setFile({ name: chosen.name, content });
      setPreview(result);
    } catch (error) {
      setFile(null);
      setPreview(null);
      toast.error(errorText(error, 'Fichier illisible'));
    } finally {
      setBusy(false);
    }
  };

  const applyImport = async (mode) => {
    if (!file) return;
    const ok = await run(async () => {
      const result = await desktopApi.importStudents({
        filename: file.name,
        content_base64: file.content,
        mode,
        confirm: mode === 'replace',
        source_label: sourceLabel.trim() || null,
      }, adminToken);
      toast.success(`Import terminé : ${result.added} ajouté(s), ${result.updated || 0} mis à jour, ${result.removed} retiré(s)`);
    });
    if (ok) {
      setFile(null);
      setPreview(null);
      setConfirmReplace(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="students-admin">
      <p className="text-sm text-muted-foreground">
        Visible par les agents connectés dans l'onglet Consignes, jamais dans un PDF, un e-mail ou un lien de partage.
        Chaque modification est inscrite dans le journal d'audit (identifiant et chambre, sans le nom).
      </p>

      <div className="space-y-3 rounded-lg border border-border p-4">
        <p className="font-semibold">Importer une liste (CSV ou Excel .xlsx)</p>
        <p className="text-sm text-muted-foreground">
          Première ligne : nom, prénom, chambre ; colonnes facultatives : lit, départ, classe, étage. Rien n'est enregistré avant votre choix.
        </p>
        <label className="inline-flex h-11 cursor-pointer items-center gap-2 rounded-md border border-border px-4 font-semibold hover:border-primary/60">
          <Upload className="h-4 w-4" /> Choisir un fichier
          <input type="file" accept=".csv,.txt,.xlsx" onChange={pickFile} className="sr-only" data-testid="students-file" />
        </label>
        {preview && (
          <div className="space-y-3" data-testid="students-preview">
            <p className="text-sm">
              <strong>{file?.name}</strong> : {preview.valid} fiche(s) valide(s)
              {preview.error_count ? `, ${preview.error_count} ligne(s) refusée(s)` : ''}
              {preview.duplicates ? `, ${preview.duplicates} doublon(s) ignoré(s)` : ''}.
            </p>
            {preview.errors?.length > 0 && (
              <ul className="max-h-32 overflow-auto rounded-md bg-red-950/40 p-3 text-sm text-red-200">
                {preview.errors.map((item) => <li key={item.line}>Ligne {item.line} : {item.message}</li>)}
              </ul>
            )}
            <div className="max-h-80 overflow-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card text-left text-xs uppercase text-muted-foreground">
                  <tr><th className="p-2">Ch.</th><th className="p-2">Lit</th><th className="p-2">Nom</th><th className="p-2">Prénom</th><th className="p-2">Départ</th></tr>
                </thead>
                <tbody>
                  {(preview.preview || []).map((row, index) => (
                    <tr key={`${row.room}-${row.bed}-${index}`} className="border-t border-border">
                      <td className="p-2 font-bold">{row.room}</td><td className="p-2">{row.bed}</td>
                      <td className="p-2 uppercase">{row.last_name}</td><td className="p-2">{row.first_name}</td><td className="p-2">{row.departure}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <label className="block max-w-lg">
              <span className="mb-1 block text-xs font-semibold text-muted-foreground">Source affichée aux agents (facultatif)</span>
              <input
                value={sourceLabel}
                onChange={(event) => setSourceLabel(event.target.value)}
                placeholder="Ex. Mis à jour le 20.10.2026, selon fiche réception"
                maxLength={200}
                className={inputClass}
                data-testid="students-source"
              />
            </label>
            {preview.simulation && (
              <p className="text-sm text-muted-foreground">
                Remplacer : {preview.simulation.replace.added} ajout(s), {preview.simulation.replace.updated} mise(s) à jour,
                {' '}{preview.simulation.replace.removed} retrait(s), les notes des étudiants gardés restent liées.
                {' '}Ajouter : {preview.simulation.append.added} ajout(s).
              </p>
            )}
            <div className="flex flex-col gap-2 sm:flex-row">
              <button type="button" disabled={busy || !preview.valid} onClick={() => applyImport('append')} className="h-11 rounded-md border border-primary px-5 font-semibold text-primary disabled:opacity-50" data-testid="students-append">
                Ajouter à la liste
              </button>
              {!confirmReplace ? (
                <button type="button" disabled={busy || !preview.valid} onClick={() => setConfirmReplace(true)} className="h-11 rounded-md bg-primary px-5 font-semibold text-primary-foreground disabled:opacity-50" data-testid="students-replace">
                  Remplacer toute la liste
                </button>
              ) : (
                <button type="button" disabled={busy} onClick={() => applyImport('replace')} className="h-11 rounded-md bg-red-600 px-5 font-bold text-white" data-testid="students-replace-confirm">
                  Confirmer : remplacer les {students.length} fiche(s) actuelles
                </button>
              )}
              <button type="button" onClick={() => { setPreview(null); setFile(null); setConfirmReplace(false); }} className="h-11 rounded-md border border-border px-5">
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Rechercher nom, prénom ou chambre"
          className={`${inputClass} sm:max-w-sm`}
          data-testid="students-admin-search"
        />
        {!adding && (
          <button type="button" onClick={() => { setAdding(true); setEditingId(''); }} className="h-11 rounded-md bg-primary px-5 font-semibold text-primary-foreground" data-testid="student-add">
            Ajouter un étudiant
          </button>
        )}
      </div>
      {adding && (
        <StudentForm
          busy={busy}
          label="Ajouter"
          onCancel={() => setAdding(false)}
          onSubmit={async (values) => { if (await run(() => desktopApi.createStudent(values, adminToken), 'Étudiant ajouté')) setAdding(false); }}
        />
      )}
      <p className="text-sm text-muted-foreground">
        {students.length} fiche(s){data?.source_label ? ` · ${data.source_label}` : ''}
      </p>
      <ul className="space-y-2">
        {filtered.map((student) => (
          <li key={student.id} className="rounded-lg border border-border p-3" data-testid="admin-student-row">
            {editingId === student.id ? (
              <StudentForm
                initial={student}
                busy={busy}
                label="Enregistrer"
                onCancel={() => setEditingId('')}
                onSubmit={async (values) => { if (await run(() => desktopApi.updateStudent(student.id, values, adminToken), 'Fiche modifiée')) setEditingId(''); }}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <span className="min-w-14 rounded bg-primary/15 px-2 py-1 text-center font-black text-primary">{student.room}{student.bed ? ` ${student.bed}` : ''}</span>
                <span className="min-w-0 flex-1"><span className="font-bold uppercase">{student.last_name}</span> {student.first_name}
                  {student.departure ? <span className="text-sm text-muted-foreground"> · départ {student.departure}</span> : null}
                </span>
                {student.warning_level ? <WarningBadge level={student.warning_level} /> : null}
                {confirmId === student.id ? (
                  <>
                    <button type="button" disabled={busy} onClick={async () => { if (await run(() => desktopApi.deleteStudent(student.id, adminToken), 'Fiche retirée')) setConfirmId(''); }} className="h-11 rounded-md bg-red-600 px-4 font-bold text-white">
                      Confirmer le retrait
                    </button>
                    <button type="button" onClick={() => setConfirmId('')} className="h-11 rounded-md border border-border px-4">Garder</button>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={() => { setEditingId(student.id); setAdding(false); }} aria-label="Modifier" className="flex h-11 w-11 items-center justify-center rounded-md border border-border hover:border-primary/60">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => setConfirmId(student.id)} aria-label="Retirer" className="flex h-11 w-11 items-center justify-center rounded-md border border-border text-red-300 hover:border-red-500/60">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
      {students.length > 0 && filtered.length === 0 && <p className="text-muted-foreground">Aucun résultat.</p>}
    </div>
  );
};

export default StudentsAdmin;
